/* eslint-disable jsx-a11y/tabindex-no-positive */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Container,
  Box,
  Tabs,
  Tab,
  Grid,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { di } from '../../state/inversify.config';
import { useQuerySync } from '../../state/queryParams/useQuerySync';
import { Summary } from './summary.dumb';
import { NextSteps } from './nextSteps.dumb';
import { Reports } from './reports';
import { MobileHeader } from '../../components/mobileHeader/mobileHeader';
import { ViewIncidentQueryService } from './viewIncident.queryParam.service';
import { ViewIncidentDomainService } from './viewIncidentDomainService';
import { AccountTypes } from '../../components/changeUser/accountTypes.enum';
import { AuthService } from '../../state/helperServices/auth/auth.service';
import { ConcussionResources } from '../../components/concussionResources/concussionResources';
import { ViewIncidentTabItems } from './viewIncidentTabItems';
import { SymptomAssessmentList } from '../../components/symptomAssessment/symptomAssessmentList';
import { SymptomAssessmentDomainService } from '../symptomAssessment/symptomAssessmentDomainService';

export function ViewIncidentPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);

  const viewIncidentQueryService = di.get<ViewIncidentQueryService>(
    ViewIncidentQueryService,
  );
  useQuerySync(viewIncidentQueryService);

  // TODO: move into viewIncidentDomainService
  const authService = di.get<AuthService>(AuthService);
  const user = authService.useLoggedInUserSelector();
  const isCoaches: boolean = user?.accountType === AccountTypes.Org;
  const isClinic: boolean = user?.accountType === AccountTypes.Clinic;
  const isGuardian: boolean = user?.accountType === AccountTypes.Guardian;

  const queryParam = viewIncidentQueryService.useQuerySelector();
  const { selectedIncidentId } = queryParam;

  const viewIncidentDomainService = di.get<ViewIncidentDomainService>(
    ViewIncidentDomainService,
  );
  const selectedPlayer = viewIncidentDomainService.useSelectedPlayerSelector();
  const selectedIncident =
    viewIncidentDomainService.useSelectedIncidentSelector();
  const selectedIncidentByPlayer =
    viewIncidentDomainService.useSelectedIncidentByPlayerSelector();
  const currentIncidentStatus =
    viewIncidentDomainService.useCurrentIncidentStatusSelector();
  const cleared = currentIncidentStatus === 'cleared';

  const symptomAssessmentDomainService = di.get<SymptomAssessmentDomainService>(
    SymptomAssessmentDomainService,
  );
  const selectedPlayerSymptomAssessments =
    symptomAssessmentDomainService.selectSymptomAssessmentsForPlayerByIncident();

  const handleChange = (event: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);
  };

  return (
    <Container sx={{ height: '100vh', pb: 6 }}>
      <MobileHeader />
      <Typography
        id="view-incident-title"
        variant="h6"
        component="h6"
        fontWeight="bold"
        sx={{ m: 1 }}
      >
        {`${selectedPlayer?.firstName} ${selectedPlayer.lastName} ${t(
          'incident.INCIDENT',
        )}`}
      </Typography>

      {isCoaches && (
        <>
          <Summary
            incidentStatus={currentIncidentStatus}
            player={selectedPlayer?.firstName}
            incidentsById={selectedIncident}
            incidentsByPlayer={selectedIncidentByPlayer}
            isCoaches={isCoaches}
          />
          <SymptomAssessmentList
            enableAssessment={!cleared}
            user={user}
            player={selectedPlayer}
            symptomChecksByPlayer={selectedPlayerSymptomAssessments}
          />
        </>
      )}

      {(isGuardian || isClinic) && (
        <Box>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            aria-label={t('accessibility.TABS')}
          >
            <Tab
              label={t('incident.INCIDENT_SUMMARY_TAB_SUMMARY')}
              id="tab-0"
              aria-controls="tabpanel-0"
              sx={{ color: theme.palette.text.primary }}
              tabIndex={0}
            />

            {isGuardian && (
              <Tab
                label={t('incident.INCIDENT_SUMMARY_TAB_NEXT_STEPS')}
                id="tab-1"
                aria-controls="tabpanel-1"
                sx={{ color: theme.palette.text.primary }}
                tabIndex={1}
              />
            )}

            <Tab
              label={t('incident.INCIDENT_SUMMARY_TAB_REPORTS')}
              id="tab-2"
              aria-controls="tabpanel-2"
              sx={{ color: theme.palette.text.primary }}
              tabIndex={2}
            />
            {/* <Tab label="Timeline" id="tab-3" aria-controls="tabpanel-3" /> */}
          </Tabs>

          <ViewIncidentTabItems value={activeTab} index={0}>
            <Summary
              incidentStatus={currentIncidentStatus}
              player={selectedPlayer?.firstName}
              incidentsById={selectedIncident}
              incidentsByPlayer={selectedIncidentByPlayer}
              isCoaches={isCoaches}
            />
          </ViewIncidentTabItems>

          <ViewIncidentTabItems value={activeTab} index={1}>
            {isGuardian && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <NextSteps player={selectedPlayer?.firstName} />
                </Grid>
                <Grid item xs={12}>
                  <ConcussionResources />
                </Grid>
              </Grid>
            )}

            {isClinic && (
              <>
                <Reports />
                <SymptomAssessmentList
                  enableAssessment={!cleared}
                  user={user}
                  player={selectedPlayer}
                  symptomChecksByPlayer={selectedPlayerSymptomAssessments}
                />
              </>
            )}
          </ViewIncidentTabItems>

          <ViewIncidentTabItems value={activeTab} index={2}>
            {isGuardian && (
              <>
                <Reports />
                <SymptomAssessmentList
                  enableAssessment={!cleared}
                  user={user}
                  player={selectedPlayer}
                  symptomChecksByPlayer={selectedPlayerSymptomAssessments}
                />
              </>
            )}
          </ViewIncidentTabItems>

          {/* <ViewIncidentTabItems value={value} index={3}>
            <Timeline incidents={selectedIncidentByPlayerSelector} />
          </ViewIncidentTabItems> */}
        </Box>
      )}
    </Container>
  );
}
